import { useState } from 'react';
import { Shield, Upload, DollarSign, FileText } from 'lucide-react';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';

const LoanCollateralForm = ({ loan, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    collateral_type: 'property',
    collateral_name: '',
    description: '',
    estimated_value: '',
    document_reference: '',
    ownership_proof: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [ltvRatio, setLtvRatio] = useState(null);

  const collateralTypes = [
    { value: 'property', label: 'Property', icon: '🏠', description: 'Land, buildings' },
    { value: 'vehicle', label: 'Vehicle', icon: '🚗', description: 'Cars, motorcycles' },
    { value: 'equipment', label: 'Equipment', icon: '⚙️', description: 'Machinery, tools' },
    { value: 'shares', label: 'Shares', icon: '📈', description: 'Company shares, stocks' },
    { value: 'savings', label: 'Savings', icon: '💰', description: 'Fixed deposits' },
    { value: 'guarantor', label: 'Guarantor', icon: '👤', description: 'Personal guarantee' },
    { value: 'other', label: 'Other', icon: '📦', description: 'Custom collateral' },
  ];

  const calculateLTV = (value) => {
    if (value && loan.principal) {
      const ratio = (parseFloat(loan.principal) / parseFloat(value)) * 100;
      setLtvRatio(ratio.toFixed(2));
    } else {
      setLtvRatio(null);
    }
  };

  const handleValueChange = (value) => {
    setFormData({ ...formData, estimated_value: value });
    calculateLTV(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (parseFloat(formData.estimated_value) < parseFloat(loan.principal)) {
      if (!window.confirm('Collateral value is less than loan principal. Continue anyway?')) {
        return;
      }
    }

    setLoading(true);
    try {
      await api.post('/loan-collaterals', {
        loan_id: loan.id,
        ...formData,
      });
      
      toast.success('Collateral added successfully');
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add collateral');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const selectedType = collateralTypes.find(t => t.value === formData.collateral_type);

  return (
    <div className="bg-white rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">
          <Shield className="w-6 h-6 mr-2 text-blue-600" />
          Add Collateral
        </h3>
        <p className="text-sm text-gray-600">
          Register collateral to secure this loan
        </p>
      </div>

      {/* Loan Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-blue-600">Loan Number</p>
            <p className="font-semibold text-blue-900">{loan.loan_no}</p>
          </div>
          <div>
            <p className="text-blue-600">Member</p>
            <p className="font-semibold text-blue-900">{loan.member?.name}</p>
          </div>
          <div>
            <p className="text-blue-600">Loan Amount</p>
            <p className="font-semibold text-blue-900">{formatCurrency(loan.principal)}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Collateral Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Collateral Type *
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {collateralTypes.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => setFormData({ ...formData, collateral_type: type.value })}
                className={`p-4 border-2 rounded-lg text-center transition-colors ${
                  formData.collateral_type === type.value
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="text-3xl mb-2">{type.icon}</div>
                <div className="font-semibold text-sm text-gray-900">{type.label}</div>
                <div className="text-xs text-gray-600 mt-1">{type.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Collateral Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Collateral Name *
          </label>
          <input
            type="text"
            value={formData.collateral_name}
            onChange={(e) => setFormData({ ...formData, collateral_name: e.target.value })}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder={`e.g., ${selectedType?.value === 'property' ? 'Plot No. 123, Nairobi' : selectedType?.value === 'vehicle' ? 'Toyota Corolla 2020' : 'Collateral name'}`}
            required
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            rows="3"
            placeholder="Detailed description of the collateral..."
          />
        </div>

        {/* Estimated Value */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Estimated Value *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <DollarSign className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="number"
              value={formData.estimated_value}
              onChange={(e) => handleValueChange(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="0.00"
              step="0.01"
              min="0"
              required
            />
          </div>
          {ltvRatio && (
            <div className="mt-2 flex items-center justify-between text-sm">
              <span className="text-gray-600">Loan-to-Value Ratio:</span>
              <span className={`font-semibold ${
                parseFloat(ltvRatio) <= 70 ? 'text-green-600' : 
                parseFloat(ltvRatio) <= 90 ? 'text-yellow-600' : 
                'text-red-600'
              }`}>
                {ltvRatio}%
              </span>
            </div>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Recommended: Collateral value should be at least equal to loan amount (LTV ≤ 100%)
          </p>
        </div>

        {/* Document Reference */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Document Reference
          </label>
          <input
            type="text"
            value={formData.document_reference}
            onChange={(e) => setFormData({ ...formData, document_reference: e.target.value })}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., Title Deed No., Registration No."
          />
        </div>

        {/* Ownership Proof */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ownership Proof
          </label>
          <div className="flex items-center space-x-3">
            <input
              type="text"
              value={formData.ownership_proof}
              onChange={(e) => setFormData({ ...formData, ownership_proof: e.target.value })}
              className="block flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Document name or reference"
            />
            <button
              type="button"
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Upload title deed, logbook, or other ownership documents
          </p>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Notes
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            rows="2"
            placeholder="Any additional information..."
          />
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <FileText className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">Next Steps</p>
              <p className="text-sm text-blue-800 mt-1">
                After adding the collateral, it will need to be verified by an admin before the loan can be disbursed. Ensure all documents are uploaded and information is accurate.
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Adding...' : 'Add Collateral'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LoanCollateralForm;
